import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import {
  useTSQUserList,
  useTSMDeactivateUser,
  useTSMReactivateUser,
} from "../../hooks/useTSUsers";
import { ListRequestFilter } from "../../types/common/commonTypes";
import { UserFull } from "../../types/userTypes";
import { useAuth } from "../../contexts/AuthContextWithQuery";
import { paginationDefaults } from "../../config/settingsDefaults";
import { InviteUserForm } from "../../components/forms/InviteUserForm";

export const PageUserList: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserFull[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("active");
  const [pagination, setPagination] = useState({
    page: 0,
    size: paginationDefaults.startingRowOption,
  });
  const [searchFilters, setSearchFilters] = useState<ListRequestFilter[][]>([
    [],
  ]);
  const [orderBy, setOrderBy] = useState<string>("name");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const { user } = useAuth();
  const thisUser = user;
  const {
    data: userResponse,
    isLoading,
    error: queryError,
    isPlaceholderData,
  } = useTSQUserList({
    skip: -1,
    limit: -1,
    active_status: statusFilter,
    filters: searchFilters,
  });

  const deactivateUserMutation = useTSMDeactivateUser();
  const reactivateUserMutation = useTSMReactivateUser();

  useEffect(() => {
    if (userResponse?.items) {
      setUsers(userResponse.items);
      setTotalCount(userResponse.total);
    }
  }, [userResponse]);

  const handleDeactivate = async (id: string) => {
    if (!window.confirm("Are you sure you want to deactivate this User?")) {
      return;
    }

    try {
      await deactivateUserMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to deactivate User:", err);
      setError(err.response?.data?.detail || "Failed to deactivate User");
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      await reactivateUserMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to reactivate User:", err);
      setError(err.response?.data?.detail || "Failed to reactivate User");
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPagination((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const getSortValue = (user: UserFull, property: string): any => {
    switch (property) {
      case "first_name":
        return user.first_name?.toLowerCase() || "";
      case "last_name":
        return user.last_name?.toLowerCase() || "";
      case "email":
        return user.email.toLowerCase();
      case "role":
        return user.role.toLowerCase();
      case "verify":
        return user.is_verified ? "yes" : "no";
      case "superuser":
        return user.is_superuser ? "yes" : "no";
      case "status":
        return user.is_record_active ? "yes" : "no";
      case "created_at":
        return new Date(user.created_at || "");
      default:
        return "";
    }
  };

  // Note: Inventory statuses are configuration data and typically don't support delete/restore operations
  // They are managed through the backend configuration and should remain stable for operational integrity

  const filteredAndSortedStatuses = useMemo(() => {
    const filtered = users.filter((user) => {
      const matchesSearch =
        searchTerm === "" ||
        user.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.last_name ? user.last_name : "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (user.first_name ? user.first_name : "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      return matchesSearch;
    });

    if (orderBy) {
      const sorted = [...filtered].sort((a, b) => {
        const aValue = getSortValue(a, orderBy);
        const bValue = getSortValue(b, orderBy);

        if (aValue === bValue) return 0;

        if (order === "asc") {
          return aValue < bValue ? -1 : 1;
        } else {
          return aValue > bValue ? -1 : 1;
        }
      });
      const start = pagination.size * pagination.page;
      const end = start + pagination.size;
      return sorted.slice(start, end);
    } else {
      const start = pagination.size * pagination.page;
      const end = start + pagination.size;
      return filtered.slice(start, end);
    }
  }, [users, searchTerm, orderBy, order, pagination]);

  if (isLoading && users.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Users
        </Typography>
        <Stack direction="row" spacing={2}>
          {/* Show Invite User button only for superusers */}
          {thisUser?.is_superuser && (
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={() => setInviteModalOpen(true)}
            >
              Invite User
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/users/form?mode=create")}
          >
            Create User
          </Button>
        </Stack>
      </Stack>

      {(error || queryError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || queryError?.message || "An unknown error occurred"}
        </Alert>
      )}

      {isPlaceholderData && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading updated data...
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Search users by first/last name or role..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sortDirection={orderBy === "first_name" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "first_name"}
                  direction={orderBy === "first_name" ? order : "asc"}
                  onClick={() => handleSort("first_name")}
                >
                  First Name
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "last_name" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "last_name"}
                  direction={orderBy === "last_name" ? order : "asc"}
                  onClick={() => handleSort("last_name")}
                >
                  Last Name
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "email" ? order : false}>
                <TableSortLabel
                  active={orderBy === "email"}
                  direction={orderBy === "email" ? order : "asc"}
                  onClick={() => handleSort("email")}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "role" ? order : false}>
                <TableSortLabel
                  active={orderBy === "role"}
                  direction={orderBy === "role" ? order : "asc"}
                  onClick={() => handleSort("role")}
                >
                  Role
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "superuser" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "superuser"}
                  direction={orderBy === "superuser" ? order : "asc"}
                  onClick={() => handleSort("superuser")}
                >
                  Is SuperUser?
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "verify" ? order : false}>
                <TableSortLabel
                  active={orderBy === "verify"}
                  direction={orderBy === "verify" ? order : "asc"}
                  onClick={() => handleSort("verify")}
                >
                  Verified?
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "status" ? order : false}>
                <TableSortLabel
                  active={orderBy === "status"}
                  direction={orderBy === "status" ? order : "asc"}
                  onClick={() => handleSort("status")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "created_at" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "created_at"}
                  direction={orderBy === "created_at" ? order : "asc"}
                  onClick={() => handleSort("created_at")}
                >
                  Created
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredAndSortedStatuses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    {searchTerm
                      ? "No users found matching your search."
                      : "No users found. This is very weird and shouldn't have happened. Please notify Josh immediately."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedStatuses.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, fontWeight: "medium" }}
                    >
                      {user.first_name ? user.first_name : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, fontWeight: "medium" }}
                    >
                      {user.last_name ? user.last_name : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, fontWeight: "medium" }}
                    >
                      {user.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, fontWeight: "medium" }}
                    >
                      {user.role}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        user.is_superuser ? <CheckCircleIcon /> : <CancelIcon />
                      }
                      color={user.is_superuser ? "success" : "default"}
                      size="small"
                      clickable={false}
                      onClick={() => {}}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={
                        user.is_verified ? <CheckCircleIcon /> : <CancelIcon />
                      }
                      color={user.is_verified ? "success" : "error"}
                      size="small"
                      clickable={false}
                      onClick={() => {}}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.is_record_active ? "Active" : "Inactive"}
                      color={user.is_record_active ? "success" : "error"}
                      size="small"
                      clickable={false}
                      onClick={() => {}}
                    />
                  </TableCell>
                  <TableCell>
                    {user.created_at &&
                      new Date(user.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(`/users/form?mode=view&id=${user.id}`)
                          }
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit User">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(`/users/form?mode=edit&id=${user.id}`)
                          }
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!user.is_record_active ? (
                        <Tooltip title="Reactivate User">
                          <IconButton
                            size="small"
                            onClick={() => handleReactivate(user.id)}
                            color="success"
                            disabled={reactivateUserMutation.isPending}
                          >
                            <RestoreIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title={
                            user.id === thisUser?.user_id
                              ? "Cannot Deactivate Own User Account"
                              : "Deactivate User"
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleDeactivate(user.id)}
                            color="error"
                            disabled={
                              deactivateUserMutation.isPending ||
                              user.id === thisUser?.user_id
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
      </TableContainer>

      {/* Invite User Modal */}
      <InviteUserForm
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onSuccess={() => {
          // Refresh user list when invitation is successfully sent
          // The user list will update automatically via TanStack Query
        }}
      />
    </Box>
  );
};
